import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useMemo } from 'react'

import type { DataTableColumn } from '@/admin/components/data-table'
import api from '@/admin/lib/api'
import { useEntityRegisteredStatus } from '@/admin/hooks/use-entity-registration'
import type { PredicateNode } from '@/admin/components/condition-builder/types'
import type { RuleHit } from '@/admin/hooks/use-validation-rules'

export type EntityAttributeSource = 'COLUMN' | 'EAV' | 'COMPUTED'
export type EntityValueSource =
  | 'manual'
  | 'registration_field'
  | 'product_attribute'
  | 'transaction_attribute'
  | 'warranty_attribute'
  | 'claim_attribute'
  | 'warranty_category'
  | 'workflow_state'
  | 'entity_relationship'

export type EntityFieldType =
  | 'TEXT'
  | 'RICH_TEXT'
  | 'NUMBER'
  | 'BOOLEAN'
  | 'SELECT'
  | 'MULTI_SELECT'
  | 'DOUBLE_SELECT'
  | 'DATE'
  | 'DATE_TIME'
  | 'DOCUMENT'
  | 'FILE'
  | 'IMAGE'
  | 'VIDEO'
  | 'LINK'
  | 'MONEY'
  | 'ADDRESS'
  | 'PHONE'
  | 'EMAIL'
  | 'ENTITY_SEARCH'

export interface EntityDisplayConfig {
  table?: boolean
  detail?: boolean
  label?: string
}

export interface EntityTableDisplayColumn {
  id: string
  entity_key: string
  attribute_code_name: string
  relationship_key?: string
  relationship_path?: string[]
  label?: string
  /**
   * Whether the column is shown by default when it has no saved user
   * preference. Omitted defaults to ``true`` (configured columns are
   * visible). Set ``false`` to seed a column as available-but-hidden in
   * the column picker - e.g. the config-injected Payment/Status workflow
   * columns, which must stay hidden by default to preserve the prior
   * hardcoded-fallback appearance.
   */
  default_visible?: boolean
}

export interface EntityMetadataDisplayConfig {
  registration_table?: {
    columns?: EntityTableDisplayColumn[]
  }
  registration_card?: {
    columns?: EntityTableDisplayColumn[]
  }
  /** Card display config for the purchase / transaction section (set via transaction templates). */
  purchase_card?: {
    columns?: EntityTableDisplayColumn[]
  }
}

export interface EntityAttributeConfig {
  value_source?: EntityValueSource
  workflow_key?: string
  relationship_key?: string
  display?: EntityDisplayConfig
  options?: Array<{ label: string; value: string }>
  // Maps a resolved computed value to a display label, e.g.
  // ``{"MANUFACTURER_WARRANTY": "Standard"}``. Applied after the
  // ``default_value`` template substitution.
  value_map?: Record<string, string>
  [key: string]: unknown
}

export interface EntityRegistrationSegmentMember {
  relationship_key: string
  target_entity_key?: string
}

export interface EntityRegistrationSegment {
  key: string
  label: string
  order: number
  members: EntityRegistrationSegmentMember[]
}

export interface EntityRegistrationCustomField {
  uuid?: string
  code_name: string
  display_name: string
  description?: string
  field_type: EntityFieldType
  required: boolean
  configuration_data?: Record<string, unknown>
  order: number
  visibility_conditions?: PredicateNode | null
  validation_rules?: Array<{
    uuid?: string
    name?: string
    conditions: PredicateNode | null
    severity?: string
    message?: string
    trigger_step?: string | null
  }>
}

export interface EntityRegistrationConfig {
  segments_enabled?: boolean
  segments?: EntityRegistrationSegment[]
  custom_fields?: EntityRegistrationCustomField[]
}

export interface EntityAttributeDefinition {
  uuid: string
  code_name: string
  display_name: string
  description: string
  field_type: EntityFieldType
  source: EntityAttributeSource
  column_name: string
  required: boolean
  editable: boolean
  configuration_data: EntityAttributeConfig
  hint: string
  order: number
}

export interface EntityRelationshipDefinition {
  uuid: string
  key: string
  label: string
  target: number
  target_entity_key?: string
  target_key: string
  target_label: string
  cardinality: 'ONE' | 'MANY'
  via: string
}

export interface EntityTypeDefinition {
  uuid: string
  key: string
  label: string
  description: string
  icon: string
  tenant: number | null
  organisation: number | null
  django_model: string
  is_registered: boolean
  metadata: Record<string, unknown> & {
    entity_tier?: string
    editor_hidden?: boolean
    parent_entity_key?: string
    reference_only?: boolean
    registration?: EntityRegistrationConfig
    display?: EntityMetadataDisplayConfig
  }
  attributes: EntityAttributeDefinition[]
  relationships: EntityRelationshipDefinition[]
  /**
   * Display sections that have a curated platform default the backend can
   * restore (drives "Reset to default" button visibility). Absent on older
   * payloads, so always read defensively.
   */
  default_display_sections?: string[]
}

export interface EntityTypeCreateInput {
  label: string
  description?: string
  icon?: string
  metadata?: EntityTypeDefinition['metadata']
}

export interface EntityRegistrationPreviewValues {
  root?: Record<string, unknown>
  attributes?: Record<string, unknown>
  custom_fields?: Record<string, unknown>
  entities?: Record<string, Record<string, unknown>>
}

export interface EntityRegistrationPreviewField {
  code_name: string
  source: 'entity_attribute' | 'custom_field'
  visible: boolean
  required: boolean
  missing_refs: string[]
  trace: string[]
}

export interface EntityRegistrationPreviewResult {
  step: string
  context: {
    fields: Record<string, unknown>
    entities: Record<string, Record<string, unknown>>
    aggregates: Record<string, unknown>
    roles: string[]
    permissions: string[]
  }
  visible_fields: EntityRegistrationPreviewField[]
  validation_hits: RuleHit[]
}

export interface EntityRecordProjection {
  uuid: string
  record_uuid: string
  entity_key: string
  label: string
  search_text: string
  source_created_at: string | null
  source_updated_at: string | null
  created_at: string
  updated_at: string
  values: Record<string, unknown>
}

export interface EntityRecordsResponse {
  count: number
  page: number
  page_size: number
  results: EntityRecordProjection[]
}

export interface EntityRecordQueryParams {
  page?: number
  page_size?: number
  search?: string
  ordering?: string
  filters?: Record<string, string | number | boolean | null | undefined>
}

const ENTITY_TYPES_KEY = ['entity-types'] as const
const ENTITY_TYPE_KEY = (key: string) => ['entity-type', key] as const
const ENTITY_RECORDS_KEY = (entityUuid: string | undefined, params?: EntityRecordQueryParams) =>
  ['entity-records', entityUuid ?? '', params ?? {}] as const

function entityRecordParams(params?: EntityRecordQueryParams) {
  const out: Record<string, string | number | boolean> = {}
  if (!params) return out
  if (params.page !== undefined) out.page = params.page
  if (params.page_size !== undefined) out.page_size = params.page_size
  if (params.search) out.search = params.search
  if (params.ordering) out.ordering = params.ordering
  Object.entries(params.filters ?? {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      out[`filter[${key}]`] = value
    }
  })
  return out
}

// Exported as shared query options so the post-login cache warmer can prefetch
// this exact query without re-declaring the key/queryFn (no drift).
export function entityTypesQueryOptions(params?: { key?: string }) {
  return queryOptions<EntityTypeDefinition[]>({
    queryKey: [...ENTITY_TYPES_KEY, params?.key ?? 'all'],
    queryFn: () =>
      api
        .get<EntityTypeDefinition[]>('/entities/admin/types/', { params })
        .then((r) => r.data ?? []),
    staleTime: 60_000,
  })
}

export function useEntityTypes(
  params?: { key?: string },
  options: { enabled?: boolean } = {},
) {
  return useQuery<EntityTypeDefinition[]>({
    ...entityTypesQueryOptions(params),
    enabled: options.enabled ?? true,
  })
}

export function useEntityTypeByKey(key: string | undefined) {
  // Only fetch the full definition once the tenant-agnostic presence
  // lookup confirms the entity is registered for this tenant. Firing
  // by-key for an unregistered entity (e.g. `host` on a host-less
  // tenant) 404s on every page mount. The lookup reads from the batched,
  // cached `/entities/lookup/` endpoint, which never 404s.
  const { registered, isResolving } = useEntityRegisteredStatus(key ?? '')
  const query = useQuery<EntityTypeDefinition>({
    queryKey: ENTITY_TYPE_KEY(key ?? ''),
    enabled: Boolean(key) && registered === true,
    queryFn: () =>
      api
        .get<EntityTypeDefinition>(`/entities/admin/types/by-key/${key}/`)
        .then((r) => r.data),
    staleTime: 60_000,
  })
  // True while it is not yet knowable whether this entity has display
  // config: the registration lookup is still in flight, or a registered
  // entity's full definition is loading for the first time. Callers gate
  // a loading indicator on this instead of flashing a hardcoded fallback
  // before the config lands (SX-229).
  const isConfigLoading =
    Boolean(key) && (isResolving || (registered === true && query.isLoading))
  return Object.assign(query, { isConfigLoading })
}

export function useUpdateEntityType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      uuid,
      ...data
    }: Partial<EntityTypeDefinition> & { uuid: string }) =>
      api.patch<EntityTypeDefinition>(`/entities/admin/types/${uuid}/`, data).then((r) => r.data),
    onSuccess: (entity) => {
      qc.invalidateQueries({ queryKey: ENTITY_TYPES_KEY })
      qc.invalidateQueries({ queryKey: ENTITY_TYPE_KEY(entity.key) })
    },
  })
}

/**
 * Restore one display section to its platform default and persist it
 * immediately. Returns the updated entity; invalidates the entity caches so
 * the Display tab re-initialises from the seeded columns.
 */
export function useResetEntityDisplayDefault() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      uuid,
      sectionKey,
    }: {
      uuid: string
      sectionKey: string
    }) =>
      api
        .post<EntityTypeDefinition>(
          `/entities/admin/types/${uuid}/display/${sectionKey}/reset/`,
        )
        .then((r) => r.data),
    onSuccess: (entity) => {
      qc.invalidateQueries({ queryKey: ENTITY_TYPES_KEY })
      qc.invalidateQueries({ queryKey: ENTITY_TYPE_KEY(entity.key) })
    },
  })
}

export function useCreateEntityType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: EntityTypeCreateInput) =>
      api.post<EntityTypeDefinition>('/entities/admin/types/', data).then((r) => r.data),
    onSuccess: (entity) => {
      qc.invalidateQueries({ queryKey: ENTITY_TYPES_KEY })
      qc.invalidateQueries({ queryKey: ENTITY_TYPE_KEY(entity.key) })
    },
  })
}

export function useDeleteEntityType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) =>
      api.delete(`/entities/admin/types/${uuid}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ENTITY_TYPES_KEY })
      qc.invalidateQueries({ queryKey: ['entity-type'] })
    },
  })
}

export function useEntityRegistrationPreview(entityUuid: string | undefined) {
  return useMutation({
    mutationFn: (body: {
      step?: string
      registration?: EntityRegistrationConfig
      values?: EntityRegistrationPreviewValues
    }) =>
      api
        .post<EntityRegistrationPreviewResult>(
          `/entities/admin/types/${entityUuid}/registration-preview/`,
          body,
        )
        .then((r) => r.data),
  })
}

export function useCreateEntityAttribute(entityUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<EntityAttributeDefinition>) =>
      api
        .post<EntityAttributeDefinition>(
          `/entities/admin/types/${entityUuid}/attributes/`,
          data,
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ENTITY_TYPES_KEY })
      qc.invalidateQueries({ queryKey: ['entity-type'] })
    },
  })
}

export function useUpdateEntityAttribute(entityUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      attrUuid,
      ...data
    }: Partial<EntityAttributeDefinition> & { attrUuid: string }) =>
      api
        .patch<EntityAttributeDefinition>(
          `/entities/admin/types/${entityUuid}/attributes/${attrUuid}/`,
          data,
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ENTITY_TYPES_KEY })
      qc.invalidateQueries({ queryKey: ['entity-type'] })
    },
  })
}

/**
 * Variant of {@link useUpdateEntityAttribute} that takes the owning
 * entity's UUID at mutation time instead of hook-setup time. Useful from
 * dialogs that may patch attributes belonging to several different
 * entities in one open/close cycle (e.g. the display-column editor,
 * which can target attributes on related entities).
 */
export function useUpdateEntityAttributeAcrossEntities() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      entityUuid,
      attrUuid,
      ...data
    }: Partial<EntityAttributeDefinition> & {
      entityUuid: string
      attrUuid: string
    }) =>
      api
        .patch<EntityAttributeDefinition>(
          `/entities/admin/types/${entityUuid}/attributes/${attrUuid}/`,
          data,
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ENTITY_TYPES_KEY })
      qc.invalidateQueries({ queryKey: ['entity-type'] })
    },
  })
}

export function useDeleteEntityAttribute(entityUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ attrUuid, force }: { attrUuid: string; force?: boolean }) =>
      api.delete(`/entities/admin/types/${entityUuid}/attributes/${attrUuid}/`, {
        params: force ? { force: 'true' } : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ENTITY_TYPES_KEY })
      qc.invalidateQueries({ queryKey: ['entity-type'] })
    },
  })
}

export function useReorderEntityAttributes(entityUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (attributes: { uuid: string; order: number }[]) =>
      api.patch(`/entities/admin/types/${entityUuid}/attributes/reorder/`, attributes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ENTITY_TYPES_KEY })
      qc.invalidateQueries({ queryKey: ['entity-type'] })
    },
  })
}

export function useCreateEntityRelationship(entityUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<EntityRelationshipDefinition>) =>
      api
        .post<EntityRelationshipDefinition>(
          `/entities/admin/types/${entityUuid}/relationships/`,
          data,
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ENTITY_TYPES_KEY })
      qc.invalidateQueries({ queryKey: ['entity-type'] })
    },
  })
}

export function useUpdateEntityRelationship(entityUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      relUuid,
      ...data
    }: Partial<EntityRelationshipDefinition> & { relUuid: string }) =>
      api
        .patch<EntityRelationshipDefinition>(
          `/entities/admin/types/${entityUuid}/relationships/${relUuid}/`,
          data,
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ENTITY_TYPES_KEY })
      qc.invalidateQueries({ queryKey: ['entity-type'] })
    },
  })
}

export function useDeleteEntityRelationship(entityUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (relUuid: string) =>
      api.delete(`/entities/admin/types/${entityUuid}/relationships/${relUuid}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ENTITY_TYPES_KEY })
      qc.invalidateQueries({ queryKey: ['entity-type'] })
    },
  })
}

export function useEntityRecords(
  entityUuid: string | undefined,
  params?: EntityRecordQueryParams,
) {
  return useQuery<EntityRecordsResponse>({
    queryKey: ENTITY_RECORDS_KEY(entityUuid, params),
    enabled: Boolean(entityUuid),
    queryFn: () =>
      api
        .get<EntityRecordsResponse>(`/entities/admin/types/${entityUuid}/records/`, {
          params: entityRecordParams(params),
        })
        .then((r) => r.data),
  })
}

export function useCreateEntityRecord(entityUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { values: Record<string, unknown>; label?: string }) =>
      api
        .post<EntityRecordProjection>(
          `/entities/admin/types/${entityUuid}/records/`,
          data,
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entity-records', entityUuid ?? ''] })
    },
  })
}

export function useUpdateEntityRecord(entityUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      recordUuid,
      ...data
    }: {
      recordUuid: string
      values: Record<string, unknown>
      label?: string
    }) =>
      api
        .patch<EntityRecordProjection>(
          `/entities/admin/types/${entityUuid}/records/${recordUuid}/`,
          data,
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entity-records', entityUuid ?? ''] })
    },
  })
}

export function useDeleteEntityRecord(entityUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (recordUuid: string) =>
      api.delete(`/entities/admin/types/${entityUuid}/records/${recordUuid}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entity-records', entityUuid ?? ''] })
    },
  })
}

export function useEntityRecordColumns(entity: EntityTypeDefinition | undefined) {
  return useMemo<DataTableColumn<EntityRecordProjection>[]>(
    () => {
      if (!entity) return []
      const attributeColumns = [...entity.attributes]
        .sort((a, b) => a.order - b.order || a.display_name.localeCompare(b.display_name))
        .map((attribute) => ({
          id: attribute.code_name,
          header: attribute.configuration_data.display?.label || attribute.display_name,
          sortField: attribute.code_name,
          defaultVisible: attribute.configuration_data.display?.table ?? true,
          cell: (row: EntityRecordProjection) => {
            const value = row.values[attribute.code_name]
            if (value === null || value === undefined || value === '') return '-'
            if (typeof value === 'object') return JSON.stringify(value)
            return String(value)
          },
          meta:
            attribute.field_type === 'SELECT' || attribute.field_type === 'MULTI_SELECT'
              ? {
                  filterType: 'select' as const,
                  filterField: attribute.code_name,
                  filterOptions: attribute.configuration_data.options ?? [],
                }
              : { filterField: attribute.code_name },
        }))

      return [
        {
          id: 'label',
          header: entity.label,
          sortField: 'label',
          cell: (row) => row.label || row.record_uuid,
        },
        ...attributeColumns,
      ]
    },
    [entity],
  )
}
